<template>
  <main>
    <!-- Header con título y estadísticas -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 class="mb-0 font-weight-bold text-dark">
              <i class="fa fa-users mr-2 text-primary"></i>
              Gestión de Socios
            </h3>
            <p class="text-muted mb-0">Administra y consulta información de socios</p>
          </div>
          <div class="text-right">
            <small class="text-muted d-block">Última actualización</small>
            <small class="text-primary font-weight-bold">{{ new Date().toLocaleString('es-CL') }}</small>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Vista de lista de socios paginada -->
    <transition
      name="fade"
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div v-if="!show.customerDetails && !show.customerCreator && !show.membershipDetail">
        <!-- Panel de búsqueda y filtros mejorado -->
        <div class="card shadow-sm border-0 mb-4">
          <div class="card-header bg-white border-0 pb-0">
            <div class="row align-items-center">
              <div class="col-12 col-md">
                <h5 class="mb-2 mb-md-0">
                  <i class="fa fa-search mr-2 text-primary"></i>
                  Búsqueda y Filtros
                </h5>
              </div>
              <div class="col-12 col-md-auto">
                <div class="d-flex justify-content-end">
                  <button 
                    class="btn btn-sm btn-outline-primary"
                    @click="refreshTable()"
                    :disabled="loading.table"
                  >
                    <i class="fa fa-refresh" :class="{ 'fa-spin': loading.table }"></i>
                    <span class="d-none d-sm-inline ml-1">{{ loading.table ? 'Actualizando...' : 'Actualizar' }}</span>
                  </button>
                  <button
                    class="btn btn-sm btn-primary ml-2"
                    @click="initiateCustomerCreator"
                    :disabled="loading.table"
                  >
                    <i class="fa fa-plus"></i>
                    <span class="d-none d-sm-inline ml-1">Nuevo Socio</span>
                  </button>
                  <button
                    class="btn btn-sm btn-success ml-2"
                    @click="handleExportSocios"
                    :disabled="loading.table || loading.exporting"
                    title="Exportar registro completo de socios y membresías a Excel"
                  >
                    <i class="fa" :class="loading.exporting ? 'fa-spinner fa-spin' : 'fa-file-excel-o'"></i>
                    <span class="d-none d-sm-inline ml-1">{{ loading.exporting ? 'Exportando...' : 'Exportar' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <!-- Búsqueda principal -->
              <div class="col-lg-4 mb-3">
                <label class="form-label small text-muted mb-1">
                  <i class="fa fa-search mr-1"></i>
                  Buscar por Nombre o RUT
                </label>
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text bg-white">
                      <i class="fa fa-search text-muted"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    class="form-control border-left-0"
                    placeholder="Ingrese nombre o RUT..."
                    v-model.trim="filters.search"
                    @keyup.enter="handleSearch"
                  />
                  <div class="input-group-append">
                    <button 
                      class="btn btn-primary" 
                      type="button"
                      @click="handleSearch"
                      :disabled="loading.table"
                    >
                      Buscar
                    </button>
                  </div>
                </div>
              </div>

              <!-- Filtros -->
              <div class="col-lg-8">
                <div class="row">
                  <!-- Sucursal -->
                  <div class="col-md-3 mb-3">
                    <label class="form-label small text-muted mb-1">
                      <i class="fa fa-building mr-1"></i>
                      Sucursal
                    </label>
                    <el-select
                      v-model="filters.sucursal"
                      placeholder="Todas las sucursales"
                      class="w-100"
                      @change="handleSearch"
                      :disabled="loading.table"
                    >
                      <el-option
                        label="Todas las sucursales"
                        value=""
                      ></el-option>
                      <el-option
                        v-for="branch in branches"
                        :key="branch._id"
                        :label="branch.nombreSucursal"
                        :value="branch._id"
                      >
                        <span class="d-flex align-items-center">
                          <i class="fa fa-building mr-2"></i>
                          {{ branch.nombreSucursal }}
                        </span>
                      </el-option>
                    </el-select>
                  </div>

                  <!-- Estado Cuenta -->
                  <div class="col-md-3 mb-3">
                    <label class="form-label small text-muted mb-1">
                      <i class="fa fa-info-circle mr-1"></i>
                      Estado Cuenta
                    </label>
                    <el-select
                      v-model="filters.estadoCuenta"
                      placeholder="Todos los estados"
                      class="w-100"
                      @change="handleSearch"
                      :disabled="loading.table || loading.catalogs"
                    >
                      <el-option
                        label="Todos los estados"
                        value=""
                      ></el-option>
                      <el-option
                        v-for="estado in catalogs.estados"
                        :key="estado._id"
                        :label="estado.glosa"
                        :value="estado._id"
                      >
                        <span class="d-flex align-items-center">
                          <span 
                            class="badge mr-2"
                            :class="getEstadoBadgeClass({ glosa: estado.glosa })"
                          >
                            &nbsp;
                          </span>
                          {{ estado.glosa }}
                        </span>
                      </el-option>
                    </el-select>
                  </div>

                  <!-- Estado Acceso -->
                  <div class="col-md-3 mb-3">
                    <label class="form-label small text-muted mb-1">
                      <i class="fa fa-lock mr-1"></i>
                      Estado Acceso
                    </label>
                    <el-select
                      v-model="filters.estadoAcceso"
                      placeholder="Todos los accesos"
                      class="w-100"
                      @change="handleSearch"
                      :disabled="loading.table || loading.catalogs"
                    >
                      <el-option
                        label="Todos los accesos"
                        value=""
                      ></el-option>
                      <el-option
                        v-for="acceso in catalogs.estadosAcceso"
                        :key="acceso._id"
                        :label="acceso.glosa"
                        :value="acceso._id"
                      >
                        <span class="d-flex align-items-center">
                          <i 
                            class="fa mr-2"
                            :class="acceso.glosa === 'AUTORIZADO' ? 'fa-check text-success' : 'fa-times text-danger'"
                          ></i>
                          {{ acceso.glosa }}
                        </span>
                      </el-option>
                    </el-select>
                  </div>

                  <!-- Perfil -->
                  <div class="col-md-3 mb-3">
                    <label class="form-label small text-muted mb-1">
                      <i class="fa fa-user-tag mr-1"></i>
                      Perfil
                    </label>
                    <el-select
                      v-model="filters.perfilCuenta"
                      placeholder="Todos los perfiles"
                      class="w-100"
                      @change="handleSearch"
                      :disabled="loading.table || loading.catalogs"
                    >
                      <el-option
                        label="Todos los perfiles"
                        value=""
                      ></el-option>
                      <el-option
                        v-for="perfil in catalogs.perfiles"
                        :key="perfil._id"
                        :label="perfil.glosa"
                        :value="perfil._id"
                      >
                        <span class="d-flex align-items-center">
                          <i class="fa fa-user-circle mr-2"></i>
                          {{ perfil.glosa }}
                        </span>
                      </el-option>
                    </el-select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chips de filtros activos -->
            <div v-if="hasActiveFilters" class="mt-3">
              <span class="text-muted small mr-2">Filtros activos:</span>
              <span v-if="filters.search" class="badge badge-pill badge-primary mr-2">
                <i class="fa fa-search mr-1"></i>
                {{ filters.search }}
                <i class="fa fa-times ml-1 pointer" @click="filters.search = ''; handleSearch()"></i>
              </span>
              <span v-if="filters.sucursal" class="badge badge-pill badge-info mr-2">
                <i class="fa fa-building mr-1"></i>
                {{ (branches.find(b => b._id === filters.sucursal) || {}).nombreSucursal }}
                <i class="fa fa-times ml-1 pointer" @click="filters.sucursal = ''; handleSearch()"></i>
              </span>
              <span v-if="filters.estadoCuenta" class="badge badge-pill badge-warning mr-2">
                <i class="fa fa-info-circle mr-1"></i>
                {{ (catalogs.estados.find(e => e._id === filters.estadoCuenta) || {}).glosa }}
                <i class="fa fa-times ml-1 pointer" @click="filters.estadoCuenta = ''; handleSearch()"></i>
              </span>
              <span v-if="filters.estadoAcceso" class="badge badge-pill badge-success mr-2">
                <i class="fa fa-lock mr-1"></i>
                {{ (catalogs.estadosAcceso.find(e => e._id === filters.estadoAcceso) || {}).glosa }}
                <i class="fa fa-times ml-1 pointer" @click="filters.estadoAcceso = ''; handleSearch()"></i>
              </span>
              <span v-if="filters.perfilCuenta" class="badge badge-pill badge-secondary mr-2">
                <i class="fa fa-user-tag mr-1"></i>
                {{ (catalogs.perfiles.find(p => p._id === filters.perfilCuenta) || {}).glosa }}
                <i class="fa fa-times ml-1 pointer" @click="filters.perfilCuenta = ''; handleSearch()"></i>
              </span>
              <button 
                class="btn btn-link btn-sm text-danger p-0 ml-2"
                @click="refreshTable()"
              >
                <i class="fa fa-times mr-1"></i>
                Limpiar todos
              </button>
            </div>
          </div>
        </div>

        <!-- Tarjetas de estadísticas -->
        <div class="row mb-4" v-if="analisis">
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card shadow-sm border-0 h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-uppercase text-muted mb-2">Total Socios</h6>
                    <h3 class="mb-0 font-weight-bold text-primary">{{ pagination.total }}</h3>
                    <small class="text-muted">
                      <i class="fa fa-info-circle mr-1"></i>
                      Registrados en el sistema
                    </small>
                  </div>
                  <div class="icon-shape bg-primary text-white rounded-circle shadow">
                    <i class="fa fa-users fa-2x p-3"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card shadow-sm border-0 h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-uppercase text-muted mb-2">Con Membresía</h6>
                    <h3 class="mb-0 font-weight-bold text-success">{{ analisis.sociosConPlan || 0 }}</h3>
                    <small class="text-success">
                      <i class="fa fa-arrow-up mr-1"></i>
                      {{ analisis.sociosConPlan && pagination.total ? 
                        ((analisis.sociosConPlan / pagination.total) * 100).toFixed(1) : 0 }}%
                    </small>
                  </div>
                  <div class="icon-shape bg-success text-white rounded-circle shadow">
                    <i class="fa fa-check-circle fa-2x p-3"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card shadow-sm border-0 h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-uppercase text-muted mb-2">Sin Membresía</h6>
                    <h3 class="mb-0 font-weight-bold text-warning">{{ analisis.sociosSinPlan || 0 }}</h3>
                    <small class="text-warning">
                      <i class="fa fa-exclamation-triangle mr-1"></i>
                      Requieren atención
                    </small>
                  </div>
                  <div class="icon-shape bg-warning text-white rounded-circle shadow">
                    <i class="fa fa-user-times fa-2x p-3"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-lg-3 col-md-6 mb-3">
            <div class="card shadow-sm border-0 h-100">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-uppercase text-muted mb-2">Filtrados</h6>
                    <h3 class="mb-0 font-weight-bold" :class="hasActiveFilters ? 'text-info' : 'text-muted'">
                      {{ hasActiveFilters ? pagination.filtered : pagination.total }}
                    </h3>
                    <small class="text-muted">
                      <i class="fa fa-filter mr-1"></i>
                      {{ hasActiveFilters ? 'Filtros activos' : 'Sin filtros' }}
                    </small>
                  </div>
                  <div class="icon-shape bg-info text-white rounded-circle shadow">
                    <i class="fa fa-filter fa-2x p-3"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabla de socios mejorada -->
        <div class="card shadow-sm border-0">
          <div class="card-header bg-white border-0">
            <div class="row align-items-center">
              <div class="col">
                <h5 class="mb-0">
                  <i class="fa fa-list mr-2 text-primary"></i>
                  Lista de Socios
                </h5>
              </div>
              <div class="col-auto">
                <small class="text-muted">
                  Mostrando 
                  <strong>{{ Math.min(pagination.perPage, socios.length) }}</strong>
                  de 
                  <strong>{{ pagination.total }}</strong>
                  registros
                </small>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <!-- Tabla de socios -->
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0 text-uppercase text-muted small font-weight-bold">
                      Socio
                    </th>
                    <th scope="col" class="border-0 text-uppercase text-muted small font-weight-bold">
                      Plan & Estado
                    </th>
                    <th scope="col" class="border-0 text-uppercase text-muted small font-weight-bold text-center">
                      Vencimiento
                    </th>
                    <th scope="col" class="border-0 text-uppercase text-muted small font-weight-bold text-center">
                      Sesiones
                    </th>
                    <th scope="col" class="border-0 text-uppercase text-muted small font-weight-bold text-center">
                      Acceso
                    </th>
                    <th scope="col" class="border-0 text-uppercase text-muted small font-weight-bold text-center">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Loading state -->
                  <tr v-if="loading.table && socios.length === 0">
                    <td colspan="6" class="text-center py-5">
                      <div class="spinner-border text-primary mb-3" role="status">
                        <span class="sr-only">Cargando...</span>
                      </div>
                      <p class="text-muted mb-0">Cargando información de socios...</p>
                    </td>
                  </tr>

                  <!-- No results -->
                  <tr v-else-if="!loading.table && socios.length === 0">
                    <td colspan="6" class="text-center py-5">
                      <i class="fa fa-search fa-3x text-muted mb-3"></i>
                      <p class="text-muted mb-1">No se encontraron socios</p>
                      <small class="text-muted">Intenta ajustar los filtros de búsqueda</small>
                    </td>
                  </tr>

                  <!-- Socios list -->
                  <tr
                    v-else
                    v-for="socio in socios"
                    :key="socio._id"
                    @click="selectSocio(socio)"
                    class="socio-row"
                  >
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="avatar-container mr-3">
                          <img
                            v-if="socio.avatar && socio.avatar.imagenMiniatura"
                            :src="`data:image/jpeg;base64,${socio.avatar.imagenMiniatura}`"
                            class="rounded-circle shadow-sm avatar-img"
                            :alt="socio.nombreSocio"
                          />
                          <div 
                            v-else 
                            class="rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm avatar-placeholder"
                          >
                            <span class="font-weight-bold">
                              {{ socio.nombreSocio ? socio.nombreSocio.charAt(0).toUpperCase() : '?' }}
                            </span>
                          </div>
                        </div>
                        <div>
                          <h6 class="mb-0 font-weight-bold">{{ socio.nombreSocio }}</h6>
                          <div class="d-flex align-items-center flex-wrap">
                            <small class="text-muted mr-3">
                              <i class="fa fa-id-card mr-1"></i>
                              {{ socio.rut }}
                            </small>
                            <small class="text-muted mr-3" v-if="socio.perfilCuenta">
                              <i class="fa fa-user-tag mr-1"></i>
                              {{ socio.perfilCuenta.glosa }}
                            </small>
                            <small class="text-primary" v-if="socio.esExtranjero">
                              <i class="fa fa-globe mr-1"></i>
                              Extranjero
                            </small>
                          </div>
                          <small class="text-muted" v-if="socio.fechaInscripcion">
                            <i class="fa fa-calendar-plus mr-1"></i>
                            Inscrito: {{ formatDate(socio.fechaInscripcion) }}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div v-if="socio.planes && socio.planes.principal">
                        <div class="mb-1">
                          <span class="font-weight-bold text-dark">{{ socio.planes.principal.descripcionPlan }}</span>
                          <span 
                            class="badge badge-pill ml-2"
                            :class="getPlanEstadoBadgeClass(socio.planes.principal.estadoPago)"
                          >
                            {{ socio.planes.principal.estadoPago.glosa }}
                          </span>
                        </div>
                        <div class="d-flex flex-wrap">
                          <small class="text-muted mr-3">
                            <i class="fa fa-dollar-sign mr-1"></i>
                            ${{ formatCurrency(socio.planes.principal.valorPlan) }}
                          </small>
                          <small class="text-muted mr-3" v-if="socio.planes.principal.convenio">
                            <i class="fa fa-handshake mr-1"></i>
                            {{ socio.planes.principal.convenio.glosa }}
                          </small>
                          <small class="text-success" v-if="socio.planes.principal.informacionAdicional && socio.planes.principal.informacionAdicional.multiSede">
                            <i class="fa fa-building mr-1"></i>
                            MultiSede
                          </small>
                        </div>
                        <small class="text-info" v-if="socio.planes.principal.turno">
                          <i class="fa fa-clock mr-1"></i>
                          {{ socio.planes.principal.turno.descripcionTurno }}
                        </small>
                      </div>
                      <div v-else>
                        <span class="badge badge-light badge-pill">
                          <i class="fa fa-times mr-1"></i>
                          Sin Membresía Asignada
                        </span>
                        <div v-if="socio.estadoCuenta && socio.estadoCuenta.glosa !== 'ACTIVA'">
                          <span 
                            class="badge badge-pill mt-1"
                            :class="getEstadoBadgeClass(socio.estadoCuenta)"
                          >
                            {{ socio.estadoCuenta.glosa }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <div v-if="socio.planes && socio.planes.principal">
                        <div class="font-weight-bold" :class="getVencimientoClass(socio.planes.principal.fechaVencimiento)">
                          {{ formatShortDate(socio.planes.principal.fechaVencimiento) }}
                        </div>
                        <small class="text-muted">
                          {{ getDaysRemaining(socio.planes.principal.fechaVencimiento) }}
                        </small>
                      </div>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="text-center">
                      <div v-if="socio.planes && socio.planes.principal && socio.planes.principal.indicadorUsoSesiones">
                        <div class="mb-1">
                          <span class="font-weight-bold">
                            {{ socio.planes.principal.sesionesUtilizadas || 0 }} / {{ socio.planes.principal.sesionesLimite }}
                          </span>
                        </div>
                        <div class="progress" style="height: 5px; width: 80px; margin: 0 auto;">
                          <div 
                            class="progress-bar"
                            :class="getSessionProgressClass(socio.planes.principal)"
                            role="progressbar" 
                            :style="`width: ${getSessionProgress(socio.planes.principal)}%`"
                          ></div>
                        </div>
                      </div>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="text-center">
                      <div>
                        <div class="mb-1">
                          <span 
                            class="badge badge-pill"
                            :class="getAccesoBadgeClass(socio.accesoSucursales)"
                          >
                            <i 
                              class="fa fa-xs mr-1"
                              :class="getEstadoAcceso(socio.accesoSucursales) === 'AUTORIZADO' ? 'fa-unlock' : 'fa-lock'"
                            ></i>
                            {{ getEstadoAcceso(socio.accesoSucursales) }}
                          </span>
                        </div>
                        <div class="d-flex justify-content-center text-muted flex-wrap">
                          <small class="mr-2" v-if="socio.credencialesAcceso && socio.credencialesAcceso.length">
                            <el-tooltip content="Tarjetas" placement="top">
                              <i class="fa fa-credit-card"></i>
                            </el-tooltip>
                            {{ socio.credencialesAcceso.length }}
                          </small>
                          <small class="mr-2" v-if="socio.clavesAcceso && socio.clavesAcceso.length">
                            <el-tooltip content="Claves" placement="top">
                              <i class="fa fa-key"></i>
                            </el-tooltip>
                            {{ socio.clavesAcceso.length }}
                          </small>
                          <small class="mr-2" v-if="getBiometriasCount(socio.biometriasAcceso, 'huella') > 0">
                            <el-tooltip content="Huellas" placement="top">
                              <i class="fa fa-fingerprint"></i>
                            </el-tooltip>
                            {{ getBiometriasCount(socio.biometriasAcceso, 'huella') }}
                          </small>
                          <small v-if="getBiometriasCount(socio.biometriasAcceso, 'rostro') > 0">
                            <el-tooltip content="Rostros" placement="top">
                              <i class="fa fa-smile"></i>
                            </el-tooltip>
                            {{ getBiometriasCount(socio.biometriasAcceso, 'rostro') }}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <button 
                        class="btn btn-sm btn-primary mr-1"
                        @click.stop="selectSocio(socio)"
                      >
                        <i class="fa fa-eye mr-1"></i>
                        Ver Detalle
                      </button>
                      <button
                        class="btn btn-sm btn-info"
                        @click.stop="goToGestionMembresias(socio)"
                      >
                        <i class="fa fa-credit-card mr-1"></i>
                        Detalle Membresía
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <!-- Paginación mejorada -->
          <div class="card-footer bg-white border-0" v-if="pagination.totalPages > 1">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <small class="text-muted">
                  Página <strong>{{ pagination.currentPage }}</strong> de <strong>{{ pagination.totalPages }}</strong>
                </small>
              </div>
              <div class="d-flex align-items-center">
                <p-pagination
                  v-model="pagination.currentPage"
                  :per-page="pagination.perPage"
                  :total="pagination.total"
                  @input="handlePageChange"
                  :disabled="loading.table"
                >
                </p-pagination>
                <div v-if="loading.table" class="ml-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="sr-only">Cargando...</span>
                  </div>
                </div>
              </div>
              <div>
                <label class="mb-0 mr-2">
                  <small class="text-muted">Registros por página:</small>
                </label>
                <el-select
                  v-model="pagination.perPage"
                  size="mini"
                  @change="handlePageChange(1)"
                  style="width: 80px;"
                  :disabled="loading.table"
                >
                  <el-option :label="10" :value="10"></el-option>
                  <el-option :label="20" :value="20"></el-option>
                  <el-option :label="50" :value="50"></el-option>
                  <el-option :label="100" :value="100"></el-option>
                </el-select>
              </div>
            </div>
          </div>
        </div>

        <!-- Métricas Avanzadas -->
        <socios-metricas :filters="filters" />
      </div>
    </transition>
    
    <!-- Vista de detalle del socio -->
    <transition
      name="fade"
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <socio-detalle
        v-if="show.customerDetails"
        ref="socioDetalle"
        :socio="showingCustomer"
        :branches="branches"
        :estados="catalogs.estados"
        :estados-acceso="catalogs.estadosAcceso"
        :perfiles="catalogs.perfiles"
        @save="handleCustomerUpdate"
        @close="closeCustomerDetails"
        @delete-socio="startCustomerDeleteModal"
        @open-avatar-modal="avatarUploadModal = true; avatarFor = 'existing'"
        @go-to-memberships="goToMemberships"
      />
    </transition>
    
    <!-- Vista de creación de socio -->
    <transition
      name="fade"
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <socio-crear
        v-if="show.customerCreator"
        :branches="branches"
        :estados="catalogs.estados"
        :estados-acceso="catalogs.estadosAcceso"
        :perfiles="catalogs.perfiles"
        :avatar-data="newCustomerAvatar"
        @create="handleCustomerCreation"
        @cancel="closeCustomerCreator"
        @open-avatar-modal="avatarUploadModal = true; avatarFor = 'new'"
      />
    </transition>
    
    <!-- Modal de gestión de avatar -->
    <socio-avatar
      :show="avatarUploadModal"
      @close="avatarUploadModal = false"
      @avatar-captured="handleAvatarCaptured"
    />
    
    <!-- Vista de detalle de membresía -->
    <transition
      name="fade"
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div v-if="show.membershipDetail">
        <!-- Header -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h4 class="mb-0 font-weight-bold text-dark">
                <i class="fa fa-credit-card mr-2 text-primary"></i>
                Detalle de Membresía
              </h4>
              <p class="text-muted mb-0">
                Visualizando membresía de <strong>{{ currentMembershipSocio.nombreSocio || '' }}</strong>
              </p>
            </div>
            <div class="text-right">
              <button 
                class="btn btn-outline-secondary"
                @click="closeMembershipDetail"
              >
                <i class="fa fa-arrow-left mr-2"></i>
                Volver a la lista
              </button>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border-0">
          <div class="card-body p-0">
            <div class="d-flex flex-column flex-md-row">
              <!-- Columna principal con detalle de membresía -->
              <div class="col-12 col-md-9 border-right">
                <socio-membresia
                  :membership="currentMembership"
                  :socio="currentMembershipSocio"
                  :catalog-familias="catalogFamilias"
                  :catalog-subfamilias="catalogSubFamilias"
                  :catalog-turnos="catalogTurnos"
                  :catalog-membresias="catalogMembresias"
                  @membership-updated="handleMembershipUpdated"
                  @membership-deleted="handleMembershipDeleted"
                  @show-shift-modal="$swal.fire('Info', 'Funcionalidad de edición de turnos en desarrollo', 'info')"
                  @assign-membership="$swal.fire('Info', 'Para asignar membresías, use el módulo de membresías', 'info')"
                  @go-to-titular="goToTitular"
                />
              </div>
              
              <!-- Columna lateral con información del socio -->
              <div class="col-12 col-md-3">
                <!-- Información del socio -->
                <div class="sticky-top p-4" style="top: 20px;">
                  <div class="text-center mb-4">
                    <img
                      v-if="currentMembershipSocio.avatar && currentMembershipSocio.avatar.imagenMiniatura"
                      class="rounded-circle shadow-sm"
                      style="width: 120px; height: 120px; object-fit: cover;"
                      :src="`data:image/jpeg;base64,${currentMembershipSocio.avatar.imagenMiniatura}`"
                      :alt="currentMembershipSocio.nombreSocio"
                    />
                    <div 
                      v-else 
                      class="rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm mx-auto"
                      style="width: 120px; height: 120px;"
                    >
                      <span class="font-weight-bold h3 mb-0">
                        {{ currentMembershipSocio.nombreSocio ? currentMembershipSocio.nombreSocio.charAt(0).toUpperCase() : '?' }}
                      </span>
                    </div>
                    <h5 class="mt-3 mb-1 font-weight-bold">{{ currentMembershipSocio.nombreSocio || "No Data" }}</h5>
                    <p class="text-muted small mb-0">{{ currentMembershipSocio.autenticacion && currentMembershipSocio.autenticacion.email || "No Email" }}</p>
                  </div>
                  
                  <hr class="my-3">
                  
                  <div class="member-details">
                    <div class="detail-item mb-3">
                      <i class="fa fa-id-card text-primary mr-2"></i>
                      <strong>RUT:</strong>
                      <div class="ml-4">{{ currentMembershipSocio.rut || "No Data" }}</div>
                    </div>
                    <div class="detail-item mb-3" v-if="currentMembershipSocio.informacionContacto">
                      <i class="fa fa-phone text-primary mr-2"></i>
                      <strong>Teléfono:</strong>
                      <div class="ml-4">
                        {{ currentMembershipSocio.informacionContacto.telefonoCelular || 
                           currentMembershipSocio.informacionContacto.telefonoFijo || "No Data" }}
                      </div>
                    </div>
                    <div class="detail-item mb-3">
                      <i class="fa fa-building text-primary mr-2"></i>
                      <strong>Sucursal:</strong>
                      <div class="ml-4">{{ currentMembershipSocio.selectedOriginBranch || "No Data" }}</div>
                    </div>
                    <div class="detail-item mb-3" v-if="currentMembershipSocio.perfilCuenta">
                      <i class="fa fa-user-tag text-primary mr-2"></i>
                      <strong>Perfil:</strong>
                      <div class="ml-4">{{ currentMembershipSocio.perfilCuenta }}</div>
                    </div>
                  </div>
                  
                  <hr class="my-3">
                  
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="btn btn-primary btn-block mb-2"
                      @click="handleOnlinePayment()"
                      v-if="couldBePayed"
                    >
                      <i class="fa fa-credit-card mr-2"></i>
                      {{ currentMembership.estadoPago === "PENDIENTE" ? "Pago en Línea" : "Renovación en Línea" }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-info btn-block mb-2"
                      @click="showMembershipHistory()"
                    >
                      <i class="fa fa-history mr-2"></i>
                      Ver Historial
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-block"
                      @click="goToSocioDetail()"
                    >
                      <i class="fa fa-user-edit mr-2"></i>
                      Editar Datos del Socio
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- Modal de confirmación de eliminación -->
    <modal :show.sync="customerDeleteModal">
      <div class="d-flex flex-column">
        <h5>Para confirmar escribe "{{ showingCustomer.rut }}"</h5>
        <small class="text-danger">Esta acción no se puede revertir!</small>
        <small class="text-danger">Eliminarás al socio, sus posibles membresías y huellas!</small>
        <div class="p-4 d-flex flex-column">
          <input
            type="text"
            placeholder="RUT DEL SOCIO...."
            class="form-control text-uppercase my-2"
            v-model="customerDeleteValidationString"
          />
          <button
            class="btn btn-danger mx-auto"
            :disabled="customerDeleteValidation"
            @click="handleCustomerDelete"
          >
            ELIMINAR
          </button>
        </div>
      </div>
    </modal>
  </main>
</template>

<script>
import PPagination from "src/components/UIComponents/Pagination.vue";
import { Badge, Modal } from "src/components/UIComponents";
import { Tooltip } from "element-ui";
import PSwitch from "src/components/UIComponents/Switch.vue";
import SocioDetalle from './components/SocioDetalle.vue';
import SocioCrear from './components/SocioCrear.vue';
import SocioAvatar from './components/SocioAvatar.vue';
import SociosMetricas from './components/SociosMetricas.vue';
import SocioMembresia from './components/SocioMembresia.vue';
import _ from "lodash";
import XLSXpopulate from "xlsx-populate";

export default {
  name: 'SociosV2',
  components: {
    PPagination,
    Badge,
    Modal,
    PSwitch,
    [Tooltip.name]: Tooltip,
    SocioDetalle,
    SocioCrear,
    SocioAvatar,
    SociosMetricas,
    SocioMembresia
  },
  data() {
    return {
      // Estados de carga
      loading: {
        table: false,
        catalogs: false,
        socio: false,
        creating: false,
        updating: false,
        deleting: false,
        exporting: false
      },
      
      // Control de exportación
      customersReportTotal: 0,
      customersReportCurrent: 0,

      // Datos principales
      socios: [],
      catalogs: {
        estados: [],
        estadosAcceso: [],
        perfiles: []
      },
      branches: [],
      analisis: null,

      // Filtros
      filters: {
        search: '',
        sucursal: '',
        estadoCuenta: '',
        estadoAcceso: '',
        perfilCuenta: ''
      },

      // Paginación
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0,
        totalPages: 0,
        filtered: 0
      },

      // Estados de visualización
      show: {
        customerCreator: false,
        customerDetails: false,
        asignMembership: false,
        membershipDetail: false,
      },

      // Modales
      avatarUploadModal: false,
      customerDeleteModal: false,
      customerDeleteValidationString: "",

      // Datos del socio actual
      showingCustomer: {},
      showingCustomerOG: {},
      newCustomer: {},
      enableCustomerEdit: false,

      // Avatar
      avatarSrc: null,
      pictureIsTaken: false,
      webcamIsReady: false,
      avatarFor: 'existing', // 'new' o 'existing'
      newCustomerAvatar: null,

      // Timer para búsqueda
      searchTimer: null,
      
      // Validación para eliminar
      customerDeleteValidation: true,
      
      // Datos de membresía actual
      currentMembership: {},
      currentMembershipSocio: {},
      
      // Catálogos para edición de membresías
      catalogFamilias: [],
      catalogSubFamilias: [],
      catalogTurnos: [],
      
      // Catálogos de membresías (unidades duración, convenios, expiración, estados)
      catalogMembresias: {
        unidadesDuracion: [],
        convenios: [],
        unidadesExpiracionRenovacion: [],
        estadosPago: []
      },
      
      // Estado para móvil
      mobileInfoExpanded: false
    };
  },
  computed: {
    hasActiveFilters() {
      return this.filters.search || 
             this.filters.sucursal || 
             this.filters.estadoCuenta || 
             this.filters.estadoAcceso || 
             this.filters.perfilCuenta;
    },
    couldBePayed() {
      const noTieneDecimales = this.currentMembership.valorPlan % 1 === 0;
      return (
        this.currentMembership.estadoPago !== "PAGADO" &&
        this.currentMembership.estadoPago !== "CONGELADO" &&
        this.currentMembership.valorPlan > 350 &&
        noTieneDecimales
      );
    }
  },
  watch: {
    'filters.search': function(newVal) {
      // Debounce para búsqueda mientras escribe
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.pagination.currentPage !== 1) {
          this.pagination.currentPage = 1;
        } else {
          this.handleSearch();
        }
      }, 500);
    },
    
    customerDeleteValidationString: function(newVal) {
      this.customerDeleteValidation = this.showingCustomer.rut !== newVal.toUpperCase();
    }
  },
  methods: {
    // Método principal para cargar socios
    async fetchSocios() {
      this.loading.table = true;
      
      try {
        const parameters = {
          page: this.pagination.currentPage,
          limit: this.pagination.perPage,
          includeAvatar: true,
          includePerfilCuenta: true,
          includeEstadoCuenta: true,
          includePlanes: true,
          includeAccesoSucursales: true,
          includeAnalisis: true
        };

        // Agregar filtros si existen
        if (this.filters.search) parameters.search = this.filters.search;
        if (this.filters.sucursal) parameters.sucursal = this.filters.sucursal;
        if (this.filters.estadoCuenta) parameters.estadoCuenta = this.filters.estadoCuenta;
        if (this.filters.estadoAcceso) parameters.estadoAcceso = this.filters.estadoAcceso;
        if (this.filters.perfilCuenta) parameters.perfilCuenta = this.filters.perfilCuenta;

        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/socios/consultar-paginados"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: parameters,
          Body: {}
        });

        if (response.data.result.code === 0) {
          const dataOut = response.data.dataOut;
          this.socios = dataOut.socios || [];
          this.pagination.total = dataOut.total || 0;
          this.pagination.totalPages = dataOut.pagination.totalPages || 0;
          this.pagination.filtered = dataOut.filtros.encontrados || 0;
          this.analisis = dataOut.analisis || null;
        } else {
          throw new Error(response.data.result.message);
        }
      } catch (error) {
        console.error('Error al cargar socios:', error);
        this.$swal.fire({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.result && error.response.data.result.message) || error.message || 'No se pudieron cargar los socios',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.loading.table = false;
      }
    },

    // Cargar catálogos
    async fetchCatalogs() {
      this.loading.catalogs = true;
      
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/socios/catalogos"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });

        if (response.data.result.code === 0) {
          const dataOut = response.data.dataOut;
          this.catalogs.estados = dataOut.estados || [];
          this.catalogs.estadosAcceso = dataOut.estadosAcceso || [];
          this.catalogs.perfiles = dataOut.perfiles || [];
        }
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
      } finally {
        this.loading.catalogs = false;
      }
    },
    
    // Cargar catálogos de membresías (unidades duración, convenios, expiración, estados)
    async fetchMembresiasCatalogo() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/membresias/catalogo"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          const dataOut = response.data.dataOut;
          this.catalogMembresias = {
            unidadesDuracion: dataOut.unidadesDuracion || [],
            convenios: dataOut.convenios || [],
            unidadesExpiracionRenovacion: dataOut.unidadesExpiracionRenovacion || [],
            estadosPago: dataOut.estadosPago || []
          };
        }
      } catch (error) {
        console.error("Error al cargar catálogos de membresías:", error);
        // No propagar el error para no afectar la carga del componente
      }
    },
    
    // Cargar catálogo de familias de planes
    async fetchFamiliasCatalog() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/planes/familia/catalogo"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          this.catalogFamilias = response.data.dataOut.familias || [];
        }
      } catch (error) {
        console.error("Error al cargar catálogo de familias:", error);
      }
    },
    
    // Cargar catálogo de subfamilias de planes
    async fetchSubFamiliasCatalog() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/planes/subfamilia/catalogo"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.result.code === 0) {
          this.catalogSubFamilias = response.data.dataOut.subfamilias || [];
        }
      } catch (error) {
        console.error("Error al cargar catálogo de subfamilias:", error);
      }
    },
    
    // Cargar catálogo de turnos
    async fetchTurnosCatalog() {
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: {
            Operacion: "/v1/turno/consultar-por-empresa"
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD")
          },
          Parameters: {},
          Body: {}
        });
        
        if (response.data.success && response.data.data) {
          this.catalogTurnos = response.data.data.turnos || [];
        }
      } catch (error) {
        console.error("Error al cargar catálogo de turnos:", error);
      }
    },

    // Manejadores de eventos
    handleSearch() {
      this.pagination.currentPage = 1;
      this.fetchSocios();
    },

    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.fetchSocios();
    },

    async refreshTable() {
      // Limpiar filtros
      this.filters = {
        search: '',
        sucursal: '',
        estadoCuenta: '',
        estadoAcceso: '',
        perfilCuenta: ''
      };
      this.pagination.currentPage = 1;
      await this.fetchSocios();
    },

    // Seleccionar socio
    selectSocio(socio) {
      this.fetchCustomerDetails(socio.rut);
    },

    // Métodos de utilidad para badges
    getEstadoBadgeClass(estadoCuenta) {
      if (!estadoCuenta) return 'badge-secondary';
      
      switch (estadoCuenta.glosa) {
        case 'ACTIVA':
          return 'badge-success';
        case 'PENDIENTE':
          return 'badge-warning';
        case 'INACTIVA':
          return 'badge-secondary';
        case 'BLOQUEADA':
          return 'badge-danger';
        case 'ELIMINADA':
          return 'badge-dark';
        default:
          return 'badge-secondary';
      }
    },

    getPlanEstadoBadgeClass(estadoPago) {
      if (!estadoPago) return 'badge-secondary';
      
      switch (estadoPago.glosa) {
        case 'VIGENTE':
          return 'badge-success';
        case 'PENDIENTE':
          return 'badge-warning';
        case 'VENCIDO':
          return 'badge-danger';
        case 'CANCELADO':
          return 'badge-dark';
        default:
          return 'badge-secondary';
      }
    },

    getAccesoBadgeClass(accesoSucursales) {
      if (!accesoSucursales || accesoSucursales.length === 0) {
        return 'badge-secondary';
      }

      const acceso = accesoSucursales[0];
      if (acceso.estadoAcceso && acceso.estadoAcceso.glosa === 'AUTORIZADO') {
        return 'badge-success';
      }
      return 'badge-danger';
    },

    getEstadoAcceso(accesoSucursales) {
      if (!accesoSucursales || accesoSucursales.length === 0) {
        return '-';
      }
      
      const acceso = accesoSucursales[0];
      return acceso.estadoAcceso ? acceso.estadoAcceso.glosa : '-';
    },

    // Métodos de formateo
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('es-CL', options);
    },

    formatShortDate(dateString) {
      if (!dateString) return '-';
      // Si viene en formato DD-MM-YYYY
      if (dateString.includes('-') && dateString.split('-')[0].length <= 2) {
        const [day, month, year] = dateString.split('-');
        return `${day}/${month}/${year}`;
      }
      // Si viene en formato ISO
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CL');
    },

    formatCurrency(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat('es-CL').format(value);
    },

    getVencimientoClass(fechaVencimiento) {
      if (!fechaVencimiento) return 'text-muted';
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let vencDate;
      // Si viene en formato DD-MM-YYYY
      if (fechaVencimiento.includes('-') && fechaVencimiento.split('-')[0].length <= 2) {
        const [day, month, year] = fechaVencimiento.split('-');
        vencDate = new Date(year, month - 1, day);
      } else {
        vencDate = new Date(fechaVencimiento);
      }
      vencDate.setHours(0, 0, 0, 0);
      
      const diffTime = vencDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return 'text-danger';
      if (diffDays <= 7) return 'text-warning';
      if (diffDays <= 30) return 'text-info';
      return 'text-success';
    },

    getDaysRemaining(fechaVencimiento) {
      if (!fechaVencimiento) return '';
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let vencDate;
      // Si viene en formato DD-MM-YYYY
      if (fechaVencimiento.includes('-') && fechaVencimiento.split('-')[0].length <= 2) {
        const [day, month, year] = fechaVencimiento.split('-');
        vencDate = new Date(year, month - 1, day);
      } else {
        vencDate = new Date(fechaVencimiento);
      }
      vencDate.setHours(0, 0, 0, 0);
      
      const diffTime = vencDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return `Vencido hace ${Math.abs(diffDays)} días`;
      if (diffDays === 0) return 'Vence hoy';
      if (diffDays === 1) return 'Vence mañana';
      return `${diffDays} días`;
    },

    getSessionProgress(plan) {
      if (!plan || !plan.sesionesLimite) return 0;
      const used = plan.sesionesUtilizadas || 0;
      const limit = plan.sesionesLimite;
      const percentage = (used / limit) * 100;
      return Math.min(Math.max(percentage, 0), 100);
    },

    getSessionProgressClass(plan) {
      const progress = this.getSessionProgress(plan);
      if (progress >= 90) return 'bg-danger';
      if (progress >= 70) return 'bg-warning';
      return 'bg-success';
    },

    getBiometriasCount(biometriasAcceso, tipo) {
      if (!biometriasAcceso || !Array.isArray(biometriasAcceso)) return 0;
      
      return biometriasAcceso.filter(biometria => {
        return biometria && biometria[tipo];
      }).length;
    },

    // Métodos CRUD
    initiateCustomerCreator() {
      this.show.customerCreator = true;
      this.show.customerDetails = false;
      this.avatarFor = 'new';
      this.newCustomerAvatar = null;
    },

    async fetchCustomerDetails(rut) {
      this.$swal.fire({
        title: "Cargando...",
        text: "Por favor espere",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      try {
        const response = await this.$MasterControlAPI.post(
          "",
          {
            Header: {
              Operacion: "/v3/user/socios/consultar",
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD"),
            },
            Parameters: {
              rut: rut,
              nombreSocio: "",
            },
            Body: {},
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        if (
          response.data.Response.Resultado.CodigoHTTP === 200 ||
          response.data.Response.Resultado.CodigoHTTP === 201
        ) {
          const foundCustomer = response.data.Response.DataOut.registros[0];
          
          // Buscar la sucursal en el array de branches para obtener el objeto completo
          const sucursalObj = this.branches.find(b => b._id === foundCustomer.sucursal);
          
          this.showingCustomer = {
            selectedOriginBranch: sucursalObj ? JSON.stringify(sucursalObj) : foundCustomer.nombreSucursal,
            nombreSocio: foundCustomer.nombreSocio,
            esExtranjero: foundCustomer.esExtranjero,
            rut: foundCustomer.rut.trim(),
            sexo: foundCustomer.sexo,
            perfilCuenta: foundCustomer.perfilCuenta.glosa,
            estadoCuenta: foundCustomer.estadoCuenta.glosa,
            sucursal: foundCustomer.sucursal,
            autenticacion: {
              email: foundCustomer.autenticacion.email,
            },
            informacionGeografica: {
              ...foundCustomer.informacionGeografica,
            },
            informacionContacto: {
              ...foundCustomer.informacionContacto,
            },
            informacionAdicional: {
              ...foundCustomer.informacionAdicional,
            },
            accesoSucursal: {
              estadoAcceso:
                foundCustomer.accesoSucursales[0].estadoAcceso.glosa,
              causalBloqueo:
                foundCustomer.accesoSucursales[0].causalBloqueo || ''
            },
            avatar: {
              imagenMiniatura:
                foundCustomer.avatar.imagenes.imagenMiniatura,
              imagenPerfil: foundCustomer.avatar.imagenes.imagenPerfil,
            },
            planes: foundCustomer.planes
          };
          
          this.show.customerDetails = true;
          this.show.customerCreator = false;
          this.avatarFor = 'existing';
          this.$swal.close();
        } else {
          throw new Error(response.data.Response.Resultado.MensajeServicio);
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error! :(",
          text: error.message || 'No se pudo cargar el socio',
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },
    
    async handleCustomerCreation(newCustomer) {
      this.$swal.fire({
        title: "Creando socio...",
        text: "Un momento por favor",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      let { selectedOriginBranch, ...createdCustomer } = newCustomer;
      
      if (!createdCustomer.avatar.imagenPerfil || !createdCustomer.avatar.imagenMiniatura) {
        createdCustomer.avatar.imagenPerfil = this.$store.getters.getAvatarPlaceHolder || '';
        createdCustomer.avatar.imagenMiniatura = this.$store.getters.getAvatarPlaceHolder || '';
      }
      
      const originBranch = JSON.parse(selectedOriginBranch);
      
      try {
        const response = await this.$MasterControlAPI.post("", {
          Header: { Operacion: "/v3/user/socios/ingresar/nuevo" },
          Auth: { Token: sessionStorage.getItem("jCxbraoQeD") },
          Parameters: {
            idSucursal: originBranch._id,
            nombreSucursal: originBranch.nombreSucursal,
          },
          Body: { ...createdCustomer },
        });
        
        if (
          response.data.Response.Resultado.CodigoHTTP === 200 ||
          response.data.Response.Resultado.CodigoHTTP === 201
        ) {
          this.$swal.fire({
            title: "Éxito!",
            text: "Socio creado de forma exitosa",
            icon: "success",
            confirmButtonColor: "#6bd098",
          }).then(() => {
            this.show.customerCreator = false;
            this.fetchCustomerDetails(createdCustomer.rut);
            this.refreshTable();
          });
        } else {
          throw new Error(response.data.Response.Resultado.MensajeServicio);
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error al crear socio",
          text: (error.response && error.response.data && error.response.data.Response && error.response.data.Response.Resultado && error.response.data.Response.Resultado.MensajeServicio) || error.message,
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },
    
    async handleCustomerUpdate(updatedCustomer) {
      this.$swal.fire({
        title: "Actualizando...",
        text: "Espera un momento",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      const { selectedOriginBranch, sucursal, rut, ...updateData } = updatedCustomer;
      
      if (typeof updateData.informacionAdicional.notificarVencimiento === "undefined") {
        updateData.informacionAdicional.notificarVencimiento = true;
      }
      
      // Procesar la sucursal seleccionada
      let idSucursal = sucursal;
      let nombreSucursal = selectedOriginBranch;
      
      // Si selectedOriginBranch es un JSON string (cuando se cambia la sucursal)
      if (selectedOriginBranch && selectedOriginBranch.startsWith('{')) {
        try {
          const sucursalObj = JSON.parse(selectedOriginBranch);
          idSucursal = sucursalObj._id;
          nombreSucursal = sucursalObj.nombreSucursal;
        } catch (e) {
          // Si falla el parse, mantener los valores originales
        }
      }
      
      try {
        const response = await this.$MasterControlAPI.post("", {
          Header: {
            Operacion: "/v3/user/socios/actualizar",
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD"),
          },
          Parameters: {
            rut: rut,
            idSucursal: idSucursal,
            nombreSucursal: nombreSucursal,
          },
          Body: { ...updateData },
        });
        
        if (
          response.data.Response.Resultado.CodigoHTTP === 200 ||
          response.data.Response.Resultado.CodigoHTTP === 201
        ) {
          this.$swal.fire({
            title: "Éxito!",
            text: "Modificación realizada con éxito",
            icon: "success",
            confirmButtonColor: "#6bd098",
          });
          // Recargar los datos del socio
          this.fetchCustomerDetails(rut);
          // Recargar la tabla manteniendo la página actual
          this.fetchSocios();
        } else {
          throw new Error(response.data.Response.Resultado.MensajeServicio);
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error al actualizar",
          text: (error.response && error.response.data && error.response.data.Response && error.response.data.Response.Resultado && error.response.data.Response.Resultado.MensajeServicio) || error.message,
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },
    
    startCustomerDeleteModal() {
      this.customerDeleteModal = true;
      this.customerDeleteValidationString = "";
    },
    
    async handleCustomerDelete() {
      this.$swal.fire({
        title: "Eliminando...",
        text: "Esto puede tomar unos segundos",
        allowOutsideClick: false,
        confirmButtonColor: "#6bd098",
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      try {
        const response = await this.$MasterControlAPI.post("", {
          Header: {
            Operacion: "/v3/user/socios/eliminar",
          },
          Auth: {
            Token: sessionStorage.getItem("jCxbraoQeD"),
          },
          Parameters: {
            rut: this.showingCustomer.rut,
            idSucursal: this.showingCustomer.sucursal,
            nombreSucursal: this.showingCustomer.selectedOriginBranch,
          },
          Body: {},
        });
        
        if (
          response.data.Response.Resultado.CodigoHTTP === 200 ||
          response.data.Response.Resultado.CodigoHTTP === 201
        ) {
          this.customerDeleteModal = false;
          this.customerDeleteValidationString = "";
          this.show.customerDetails = false;
          this.showingCustomer = {};
          
          this.$swal.fire({
            title: "Éxito!",
            text: "Socio eliminado correctamente!",
            icon: "success",
            confirmButtonColor: "#6bd098",
          });
          
          this.refreshTable();
        } else {
          throw new Error(response.data.Response.Resultado.MensajeServicio);
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error al eliminar",
          text: (error.response && error.response.data && error.response.data.Response && error.response.data.Response.Resultado && error.response.data.Response.Resultado.MensajeServicio) || error.message,
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },
    
    handleAvatarCaptured(avatarData) {
      if (this.avatarFor === 'new') {
        this.newCustomerAvatar = avatarData;
      } else {
        // Para socio existente, crear un nuevo objeto para asegurar reactividad
        this.showingCustomer = {
          ...this.showingCustomer,
          avatar: {
            imagenMiniatura: avatarData.imagenMiniatura,
            imagenPerfil: avatarData.imagenPerfil
          }
        };
        
        // Llamar directamente al método del componente hijo
        this.$nextTick(() => {
          if (this.$refs.socioDetalle && this.$refs.socioDetalle.handleAvatarUpdate) {
            this.$refs.socioDetalle.handleAvatarUpdate(avatarData);
          }
        });
      }
    },
    
    closeCustomerDetails() {
      this.show.customerDetails = false;
      this.showingCustomer = {};
      this.showingCustomerOG = {};
      // Recargar la tabla manteniendo la página actual
      this.fetchSocios();
    },
    
    closeCustomerCreator() {
      this.show.customerCreator = false;
      this.newCustomerAvatar = null;
    },
    
    goToMemberships() {
      this.$store.commit(
        "setCosturmerNIDcustomersAndMemberhipsFilter",
        this.showingCustomer.rut
      );
      this.$router.push("/customers&memberships/memberships");
    },

    goToGestionMembresias(socio) {
      const rut = socio && socio.rut ? socio.rut : (this.showingCustomer && this.showingCustomer.rut);
      this.$router.push({
        name: "GestionMembresias",
        query: rut ? { rut } : {}
      });
    },

    async showMembershipDetail(socio) {
      // Primero obtenemos los detalles completos del socio para tener la membresía actualizada
      this.$swal.fire({
        title: "Cargando...",
        text: "Obteniendo información de la membresía",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      try {
        // Cargar catálogos necesarios para la edición en paralelo
        await Promise.all([
          this.fetchFamiliasCatalog(),
          this.fetchSubFamiliasCatalog(),
          this.fetchTurnosCatalog(),
          this.fetchMembresiasCatalogo()
        ]);
        
        // Llamamos a la API para obtener los detalles completos del socio y su membresía
        const response = await this.$MasterControlAPI.post(
          "",
          {
            Header: {
              Operacion: "/v3/user/socios/consultar",
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD"),
            },
            Parameters: {
              rut: socio.rut,
              nombreSocio: "",
            },
            Body: {},
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        if (
          response.data.Response.Resultado.CodigoHTTP === 200 ||
          response.data.Response.Resultado.CodigoHTTP === 201
        ) {
          const foundCustomer = response.data.Response.DataOut.registros[0];
          
          // Verificar si tiene membresía principal
          if (foundCustomer.planes && foundCustomer.planes.principal) {
            // Mapear datos del socio
            this.currentMembershipSocio = {
              ...socio,
              ...foundCustomer,
              nombreSocio: foundCustomer.nombreSocio,
              rut: foundCustomer.rut,
              perfilCuenta: foundCustomer.perfilCuenta.glosa,
              autenticacion: foundCustomer.autenticacion,
              informacionContacto: foundCustomer.informacionContacto,
              informacionAdicional: foundCustomer.informacionAdicional,
              avatar: foundCustomer.avatar && foundCustomer.avatar.imagenes ? foundCustomer.avatar.imagenes : null
            };
            
            // Mapear la membresía principal
            this.currentMembership = foundCustomer.planes.principal;
            
            // Mostrar el modal
            this.show.membershipDetail = true;
            this.mobileInfoExpanded = false; // Iniciar colapsado en móvil
            this.$swal.close();
          } else {
            throw new Error("Este socio no tiene membresía asignada");
          }
        } else {
          throw new Error(response.data.Response.Resultado.MensajeServicio);
        }
      } catch (error) {
        this.$swal.fire({
          title: "Error",
          text: error.message || 'No se pudo cargar la información de la membresía',
          icon: "error",
          confirmButtonColor: "#6bd098",
        });
      }
    },
    
    handleMembershipUpdated(updatedMembership) {
      // Actualizar la membresía actual
      this.currentMembership = updatedMembership;
      // Recargar la tabla de socios
      this.fetchSocios();
    },
    
    handleMembershipDeleted() {
      // Cerrar la vista de membresía
      this.show.membershipDetail = false;
      // Limpiar datos
      this.currentMembership = {};
      this.currentMembershipSocio = {};
      // Recargar la tabla
      this.refreshTable();
    },
    
    async handleOnlinePayment() {
      // Implementar pago en línea
      this.$swal.fire({
        title: 'Pago en Línea',
        text: 'Esta funcionalidad se integrará con el módulo de pagos existente',
        icon: 'info',
        confirmButtonColor: '#6bd098'
      });
    },
    
    showMembershipHistory() {
      // Mostrar historial de membresías
      this.$swal.fire({
        title: 'Historial de Membresías',
        text: 'Esta funcionalidad mostrará el historial completo de membresías del socio',
        icon: 'info',
        confirmButtonColor: '#6bd098'
      });
    },
    
    goToTitular(rutTitular) {
      // Cerrar vista de membresía actual
      this.show.membershipDetail = false;
      // Limpiar datos
      this.currentMembership = {};
      this.currentMembershipSocio = {};
      // Buscar al titular
      this.fetchCustomerDetails(rutTitular);
    },
    
    goToSocioDetail() {
      // Cerrar la vista de membresía
      this.show.membershipDetail = false;
      // Mostrar el detalle del socio
      this.selectSocio(this.currentMembershipSocio);
    },
    
    closeMembershipDetail() {
      // Cerrar la vista de membresía
      this.show.membershipDetail = false;
      // Limpiar datos
      this.currentMembership = {};
      this.currentMembershipSocio = {};
      // Recargar la tabla manteniendo la página actual
      this.fetchSocios();
    },

    // ========================================
    // MÉTODOS DE EXPORTACIÓN
    // ========================================

    // Método principal para manejar la exportación
    async handleExportSocios() {
      try {
        this.loading.exporting = true;
        await this.renderCustomersReportXLSX('ListaPersonas_SinUpdateAutomatico');
      } catch (error) {
        console.error('Error en exportación:', error);
      } finally {
        this.loading.exporting = false;
      }
    },

    // Métodos auxiliares para limpiar datos
    cleanString(input) {
      const pattern = /[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ.,\s-]/g;
      return input.replace(pattern, "").trim();
    },
    
    cleanNum(input) {
      const pattern = /[^0-9]/g;
      return input.replace(pattern, "").trim();
    },
    
    cleanEmail(input) {
      const pattern = /[^a-zA-Z0-9.@\-_]+/g;
      return input.replace(pattern, "").trim();
    },

    // Obtener plantilla Excel
    async fetchTemplate(templateName) {
      const template = templateName;
      try {
        const response = await this.$XLSXTemplateDownload.post(
          "",
          {
            template: template,
          },
          {
            headers: {
              "Content-Type": "application/json",
              token: sessionStorage.getItem("jCxbraoQeD"),
            },
            responseType: "arraybuffer",
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error al cargar el archivo xlsx:", error);
        throw error;
      }
    },

    // Obtener datos de página específica con nuevo endpoint
    async fetchCustomerReportData(page, limit = 100) {
      console.log("fetching page n°: ", page);
      try {
        const response = await this.$CentralGymOPS_API.post(
          "",
          {
            Header: {
              Operacion: "/v1/socios/consultar-paginados/reporte-global"
            },
            Auth: {
              Token: sessionStorage.getItem("jCxbraoQeD")
            },
            Parameters: {
              page: page,
              limit: limit
            },
            Body: {}
          },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );
        
        // Retornar estructura con socios y paginación
        return {
          socios: response.data.dataOut.socios,
          pagination: response.data.dataOut.pagination
        };
      } catch (error) {
        console.error("error - fetchCustomerReportData");
        console.error(error);
        throw error;
      }
    },

    // Crear datos para el reporte Excel
    async createXLSXCustomerReportData() {
      let collectedData = [];
      this.customersReportCurrent = 0;
      this.customersReportTotal = 0;

      const inicio = new Date().getTime();
      let currentPage = 0;
      let hasNextPage = true;

      try {
        // Obtener primera página para conocer el total
        let responseData = await this.fetchCustomerReportData(currentPage, 100);
        
        // Establecer total de registros desde la paginación
        this.customersReportTotal = responseData.pagination.totalRecords;
        
        this.$swal.fire({
          title: "Descargando registros...",
          text: `${collectedData.length} de ${this.customersReportTotal}`,
          timerProgressBar: true,
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });
        
        // Loop mientras haya más páginas
        while (hasNextPage) {
          // Agregar socios de la página actual
          collectedData = [...collectedData, ...responseData.socios];
          this.customersReportCurrent = collectedData.length;
          
          // Actualizar progreso
          this.$swal.update({
            text: `${collectedData.length} de ${this.customersReportTotal}`,
          });
          this.$swal.showLoading();
          
          // Verificar si hay siguiente página
          hasNextPage = responseData.pagination.hasNextPage;
          
          if (hasNextPage) {
            currentPage++;
            responseData = await this.fetchCustomerReportData(currentPage, 100);
          }
        }
        
        console.log(`✅ Descarga completada: ${collectedData.length} registros en ${currentPage + 1} páginas`);
        
      } catch (error) {
        console.error("error - createXLSXCustomerReportData");
        console.error(error);
        throw error;
      } finally {
        const fin = new Date().getTime();
        const tiempoEjecucion = (fin - inicio) / 1000;
        console.log(`⏱️ Tiempo de descarga: ${tiempoEjecucion.toFixed(2)} segundos`);
        return collectedData;
      }
    },

    // Renderizar reporte Excel
    async renderCustomersReportXLSX(templateName) {
      this.$swal.fire({
        title: "Creando plantilla...",
        text: "Por favor espera",
        timerProgressBar: true,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });
      
      let data;

      try {
        const template = await this.fetchTemplate(templateName);
        data = await this.createXLSXCustomerReportData();

        if (data.length > 0) {
          this.$swal.fire({
            title: "Generando reporte...",
            text: "Por favor espera",
            timerProgressBar: true,
            allowOutsideClick: false,
            didOpen: () => {
              this.$swal.showLoading();
            },
          });
          
          await XLSXpopulate.fromDataAsync(template)
            .then((wb) => {
              let lastRow = 6 + data.length;
              wb.definedName("ConteoPlanes", `'DATOS'!$AK$6:$AV${lastRow}`);
              wb.definedName("CantidadPersonas", `'DATOS'!$G$6:$G${lastRow}`);
              wb.definedName("ConteoEstados", `'DATOS'!$AV$6:$AV${lastRow}`);
              wb.definedName("ConteoPersonal", `'DATOS'!$O$6:$O${lastRow}`);
              
              wb.sheet(1)
                .cell(`D3`)
                .value(this.$moment().format("DD/MM/YYYY - HH:mm:ss"));
                
              data.forEach((customer, index) => {
                wb.sheet(1)
                  .cell(`C${index + 7}`)
                  .value(parseInt(index + 1));
                  
                if (customer.informacionAdicional && customer.informacionAdicional.contrato) {
                  wb.sheet(1)
                    .cell(`D${index + 7}`)
                    .value(this.cleanString(`${customer.informacionAdicional.contrato}`));
                }
                
                if (customer.nombreSocio) {
                  wb.sheet(1)
                    .cell(`E${index + 7}`)
                    .value(this.cleanString(`${customer.nombreSocio}`));
                }
                
                if (customer.rut) {
                  wb.sheet(1)
                    .cell(`F${index + 7}`)
                    .value(this.cleanString(`${customer.rut}`));
                }
                
                if (customer.sexo) {
                  wb.sheet(1)
                    .cell(`G${index + 7}`)
                    .value(this.cleanString(`${customer.sexo}`));
                }
                
                if (customer.informacionGeografica && customer.informacionGeografica.direccion) {
                  wb.sheet(1)
                    .cell(`H${index + 7}`)
                    .value(this.cleanString(`${customer.informacionGeografica.direccion}`));
                }
                
                if (customer.informacionGeografica && customer.informacionGeografica.ciudad) {
                  wb.sheet(1)
                    .cell(`I${index + 7}`)
                    .value(this.cleanString(`${customer.informacionGeografica.ciudad}`));
                }
                
                if (customer.informacionGeografica && customer.informacionGeografica.region) {
                  wb.sheet(1)
                    .cell(`J${index + 7}`)
                    .value(this.cleanString(`${customer.informacionGeografica.region}`));
                }
                
                if (customer.informacionAdicional && customer.informacionAdicional.comuna) {
                  wb.sheet(1)
                    .cell(`K${index + 7}`)
                    .value(this.cleanString(`${customer.informacionAdicional.comuna}`));
                }
                
                if (customer.informacionContacto && customer.informacionContacto.telefonoFijo) {
                  wb.sheet(1)
                    .cell(`L${index + 7}`)
                    .value(parseInt(this.cleanNum(`${customer.informacionContacto.telefonoFijo}`)));
                }
                
                if (customer.informacionContacto && customer.informacionContacto.celular) {
                  wb.sheet(1)
                    .cell(`M${index + 7}`)
                    .value(parseInt(this.cleanNum(`${customer.informacionContacto.celular}`)));
                }
                
                if (customer.autenticacion && customer.autenticacion.email) {
                  wb.sheet(1)
                    .cell(`N${index + 7}`)
                    .value(this.cleanEmail(`${customer.autenticacion.email}`));
                }
                
                if (customer.perfilCuenta && customer.perfilCuenta.glosa) {
                  wb.sheet(1)
                    .cell(`O${index + 7}`)
                    .value(this.cleanString(`${customer.perfilCuenta.glosa}`));
                }
                
                if (customer.informacionAdicional && customer.informacionAdicional.rutTitularConvenio) {
                  wb.sheet(1)
                    .cell(`P${index + 7}`)
                    .value(this.cleanString(`${customer.informacionAdicional.rutTitularConvenio}`));
                }
                
                if (customer.fechaInscripcion) {
                  wb.sheet(1)
                    .cell(`Q${index + 7}`)
                    .value(customer.fechaInscripcion ? this.$moment(customer.fechaInscripcion).toDate() : this.$moment("01-01-1900").toDate());
                }
                
                if (customer.informacionAdicional && customer.informacionAdicional.accesoFicha) {
                  wb.sheet(1)
                    .cell(`R${index + 7}`)
                    .value(this.cleanString(`${customer.informacionAdicional.accesoFicha}`));
                }
                
                if (customer.informacionAdicional && customer.informacionAdicional.causaBloqueo) {
                  wb.sheet(1)
                    .cell(`S${index + 7}`)
                    .value(this.cleanString(`${customer.informacionAdicional.causaBloqueo}`));
                }
                
                if (customer.informacionAdicional && customer.informacionAdicional.fechaNacimiento) {
                  wb.sheet(1)
                    .cell(`T${index + 7}`)
                    .value(this.$moment(customer.informacionAdicional.fechaNacimiento).toDate());
                }
                
                if (customer.informacionContacto && customer.informacionContacto.facebook) {
                  wb.sheet(1)
                    .cell(`U${index + 7}`)
                    .value(this.cleanString(`${customer.informacionContacto.facebook}`));
                }
                
                if (customer.informacionContacto && customer.informacionContacto.instagram) {
                  wb.sheet(1)
                    .cell(`V${index + 7}`)
                    .value(this.cleanString(`${customer.informacionContacto.instagram}`));
                }
                
                if (customer.informacionContacto && (customer.informacionContacto.twitter || customer.informacionContacto.linkedin)) {
                  wb.sheet(1)
                    .cell(`W${index + 7}`)
                    .value(this.cleanString(`${customer.informacionContacto.twitter}`) || this.cleanString(`${customer.informacionContacto.linkedin}`));
                }

                // ACTUALIZADO: Usar planes.principal en lugar de plan
                const plan = customer.planes?.principal || customer.plan;
                if (plan) {
                  // Validaciones seguras para propiedades anidadas
                  if (plan.convenio?.glosa) {
                    wb.sheet(1)
                      .cell(`AK${index + 7}`)
                      .value(this.cleanString(`${plan.convenio.glosa}`));
                  }
                  
                  if (plan.nombreFamilia) {
                    wb.sheet(1)
                      .cell(`AL${index + 7}`)
                      .value(this.cleanString(`${plan.nombreFamilia}`));
                  }
                  
                  if (plan.nombreSubFamilia) {
                    wb.sheet(1)
                      .cell(`AM${index + 7}`)
                      .value(this.cleanString(`${plan.nombreSubFamilia}`));
                  }
                  
                  if (plan.descripcionPlan) {
                    wb.sheet(1)
                      .cell(`AN${index + 7}`)
                      .value(this.cleanString(`${plan.descripcionPlan}`));
                  }
                  
                  if (plan.valorPlan !== undefined) {
                    wb.sheet(1)
                      .cell(`AO${index + 7}`)
                      .value(`${parseFloat(plan.valorPlan)}` || "0");
                  }
                  
                  wb.sheet(1)
                    .cell(`AP${index + 7}`)
                    .value(plan.indicadorMatricula ? "SI" : "NO");
                    
                  if (plan.valorMatricula !== undefined) {
                    wb.sheet(1)
                      .cell(`AQ${index + 7}`)
                      .value(parseFloat(plan.valorMatricula));
                  }
                    
                  if (plan.fechaUltimoPago) {
                    wb.sheet(1)
                      .cell(`AR${index + 7}`)
                      .value(plan.fechaUltimoPago ? this.$moment(plan.fechaUltimoPago, "DD-MM-YYYY").toDate() : this.$moment("01-01-1900").toDate());
                  }
                  
                  wb.sheet(1)
                    .cell(`AS${index + 7}`)
                    .value(parseFloat(plan.porcentajeDescuento) || 0);
                  wb.sheet(1)
                    .cell(`AT${index + 7}`)
                    .value(plan.indicadorFactura ? "SI" : "NO");
                    
                  if (plan.tipoIVA) {
                    wb.sheet(1)
                      .cell(`AU${index + 7}`)
                      .value(this.cleanString(`${plan.tipoIVA}`));
                  }
                  
                  if (plan.estadoPago?.glosa) {
                    wb.sheet(1)
                      .cell(`AV${index + 7}`)
                      .value(this.cleanString(`${plan.estadoPago.glosa}`));
                  }
                  
                  wb.sheet(1)
                    .cell(`AW${index + 7}`)
                    .value(plan.fechaAsignacion ? this.$moment(plan.fechaAsignacion).toDate() : this.$moment("01-01-1900").toDate());
                  wb.sheet(1)
                    .cell(`AX${index + 7}`)
                    .value(plan.fechaInicio ? this.$moment(plan.fechaInicio, "DD-MM-YYYY").toDate() : this.$moment("01-01-1900").toDate());
                  wb.sheet(1)
                    .cell(`AY${index + 7}`)
                    .value(plan.fechaVencimiento ? this.$moment(plan.fechaVencimiento, "DD-MM-YYYY").toDate() : this.$moment("01-01-1900").toDate());
                  wb.sheet(1)
                    .cell(`AZ${index + 7}`)
                    .value(plan.fechaVigencia ? this.$moment(plan.fechaVigencia, "DD-MM-YYYY").toDate() : this.$moment("01-01-1900").toDate());
                  wb.sheet(1)
                    .cell(`BA${index + 7}`)
                    .value(plan.fechaUltimoPago ? this.$moment(plan.fechaUltimoPago, "DD-MM-YYYY").toDate() : this.$moment("01-01-1900"));
                  wb.sheet(1)
                    .cell(`BB${index + 7}`)
                    .value(plan.horaUltimoPago || "00:00:00");
                  wb.sheet(1)
                    .cell(`BC${index + 7}`)
                    .value(parseInt(plan.duracionPlan) || 0);
                    
                  if (plan.unidadDuracionPlan?.glosa) {
                    wb.sheet(1)
                      .cell(`BD${index + 7}`)
                      .value(this.cleanString(`${plan.unidadDuracionPlan.glosa}`));
                  }
                  
                  wb.sheet(1)
                    .cell(`BE${index + 7}`)
                    .value(plan.indicadorMesCalendario ? "SI" : "NO");
                  wb.sheet(1)
                    .cell(`BF${index + 7}`)
                    .value(plan.indicadorUsoSesiones ? "SI" : "NO");
                  wb.sheet(1)
                    .cell(`BG${index + 7}`)
                    .value(parseInt(plan.sesionesUtilizadas) || 0);
                  wb.sheet(1)
                    .cell(`BH${index + 7}`)
                    .value(parseInt(plan.sesionesLimite) || 0);
                  wb.sheet(1)
                    .cell(`BI${index + 7}`)
                    .value(plan.indicadorPermiteRenovar ? "SI" : "NO");
                  wb.sheet(1)
                    .cell(`BJ${index + 7}`)
                    .value(parseInt(plan.expiracionRenovacion) || 0);
                    
                  if (plan.unidadExpiracionRenovacion?.glosa) {
                    wb.sheet(1)
                      .cell(`BK${index + 7}`)
                      .value(this.cleanString(`${plan.unidadExpiracionRenovacion.glosa}`));
                  }
                  
                  wb.sheet(1)
                    .cell(`BL${index + 7}`)
                    .value(parseInt(plan.diasPermitidoCongelar) || 0);
                    
                  if (plan.turno?.idTurno) {
                    wb.sheet(1)
                      .cell(`BM${index + 7}`)
                      .value(`${plan.turno.idTurno}` || "0");
                  }
                    
                  if (plan.sucursalUltimoPago) {
                    wb.sheet(1)
                      .cell(`BN${index + 7}`)
                      .value(this.cleanString(`${plan.sucursalUltimoPago}`));
                  }
                  
                  if (plan.fechaHoraUltimoAcceso) {
                    wb.sheet(1)
                      .cell(`BO${index + 7}`)
                      .value(plan.fechaHoraUltimoAcceso ? this.$moment(plan.fechaHoraUltimoAcceso, "DD-MM-YYYY").toDate() : this.$moment("01-01-1900").toDate());
                  }
                  
                  if (plan.nombreSucursalAsignacion) {
                    wb.sheet(1)
                      .cell(`BP${index + 7}`)
                      .value(this.cleanString(`${plan.nombreSucursalAsignacion}`));
                  }
                  
                  if (plan.nombreUsuarioAsignacion) {
                    wb.sheet(1)
                      .cell(`BQ${index + 7}`)
                      .value(this.cleanString(`${plan.nombreUsuarioAsignacion}`));
                  }
                }
              });
              
              wb.outputAsync()
                .then((blob) => {
                  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(
                      blob,
                      `Base de Datos - Personas Registradas ${this.$moment().format("DD-MM-yyyy")}.xlsx`
                    );
                  } else {
                    var url = window.URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.href = url;
                    a.download = `Base de Datos - Personas Registradas ${this.$moment().format("DD-MM-yyyy")}.xlsx`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  }
                })
                .catch((ex) => {
                  this.$swal.fire({
                    title: "Error :(!",
                    text: ex.message || "Ha ocurrido un error durante la descarga, ponte en contacto con soporte",
                    icon: "error",
                  });
                });
            })
            .catch((ex) => {
              console.error("WorkBook Error");
              console.error(ex);
              this.$swal.fire({
                title: "Error :(!",
                text: "Ha ocurrido un error durante la generación del reporte, vuelve a intentarlo, si el problema persiste contactate con soporte",
                icon: "error",
              });
            });
            
          this.$swal.close();
          this.customersReportTotal = 0;
          this.customersReportCurrent = 0;
          this.$swal.fire({
            title: "EXITO!",
            text: "Reporte generado de exitosamente",
            icon: "success",
            confirmButtonColor: "#6bd098",
            confirmButtonText: "ok",
          });
        }
      } catch (ex) {
        console.error("error - renderCustomersReportXLSX");
        console.error(ex);
        this.$swal.fire({
          title: "Error :(!",
          text: ex.message || "Ha ocurrido un error durante la generación del reporte, vuelve a intentarlo, si el problema persiste contactate con soporte",
          icon: "error",
        });
      }
    }
  },

  async mounted() {
    // Minimizar sidebar si está expandido
    if (this.$sidebar.isMinimized) {
      this.$sidebar.toggleMinimize();
    }

    // Cargar sucursales desde sesión
    this.branches = [...JSON.parse(sessionStorage.getItem("xxxwypt5ip") || '[]')];

    // Cargar datos iniciales
    await Promise.all([
      this.fetchCatalogs(),
      this.fetchSocios()
    ]);
  }
};
</script>

<style scoped>
/* Botones y acciones */
.round-button {
  height: 50px;
  width: 50px;
}

.pointer {
  cursor: pointer;
}

/* Filas de tabla */
.socio-row {
  transition: all 0.3s ease;
  cursor: pointer;
}

.socio-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

/* Iconos circulares de estadísticas */
.icon-shape {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Gradiente para avatares */
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Mejoras para las cards */
.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
}

/* Badge mejorados */
.badge-pill {
  padding: 0.375em 0.75em;
  font-weight: 500;
}

/* Select personalizado */
.el-select {
  width: 100%;
}

.el-select >>> .el-input__inner {
  border-radius: 0.25rem;
  border-color: #ced4da;
  font-size: 0.875rem;
}

.el-select >>> .el-input__inner:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

/* Mejoras para la tabla */
.table {
  font-size: 0.875rem;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.table tbody td {
  vertical-align: middle;
  padding: 1rem 0.75rem;
}

/* Avatar container */
.avatar-container {
  position: relative;
  flex-shrink: 0;
}

/* Avatar images */
.avatar-img {
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  object-fit: cover;
}

.avatar-placeholder {
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
}

/* Paginación personalizada */
.card-footer {
  background-color: #f8f9fa !important;
}

/* Animaciones de entrada */
.animate__fadeIn {
  animation-duration: 0.5s;
}

/* Form labels */
.form-label {
  font-weight: 500;
  color: #495057;
}

/* Input groups mejorados */
.input-group-text {
  border: 1px solid #ced4da;
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

/* Chips de filtros */
.badge-pill i.fa-times {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.badge-pill:hover i.fa-times {
  opacity: 1;
}

/* Card shadow suave */
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}

/* Progress bars */
.progress {
  background-color: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.6s ease;
  border-radius: 10px;
}

/* Plan badges */
.badge-plan-vigente {
  background-color: #28a745;
  color: white;
}

.badge-plan-pendiente {
  background-color: #ffc107;
  color: #212529;
}

.badge-plan-vencido {
  background-color: #dc3545;
  color: white;
}

/* Table cell vertical alignment */
.table td {
  vertical-align: middle;
}

/* Small text utilities */
.table small {
  font-size: 0.75rem;
}

/* Iconos de acceso */
.table td small i {
  font-size: 0.875rem;
  margin-right: 2px;
}

/* Colores específicos para biometrías */
.fa-fingerprint {
  color: #17a2b8;
}

.fa-smile {
  color: #28a745;
}

/* Responsive table font */
@media (max-width: 1200px) {
  .table {
    font-size: 0.8125rem;
  }
  
  .table small {
    font-size: 0.7rem;
  }
}

/* Spinner de paginación */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

/* Estilos para vista de detalle de membresía */
.sticky-top {
  position: sticky;
  z-index: 10;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: box-shadow 0.3s ease;
}

.sticky-top:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.member-details .detail-item {
  font-size: 0.95rem;
  line-height: 1.6;
}

.action-buttons .btn {
  font-weight: 500;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
}

.action-buttons .btn i {
  font-size: 0.875rem;
}

/* Responsividad móvil */
@media (max-width: 768px) {
  /* Estilos generales de la tabla */
  .icon-shape {
    width: 50px;
    height: 50px;
  }
  
  .table {
    font-size: 0.75rem;
  }
  
  .card-body {
    padding: 1rem;
  }
  
  /* Avatares más pequeños en móvil */
  .avatar-img,
  .avatar-placeholder {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
  
  .avatar-placeholder span {
    font-size: 0.875rem;
  }
  
  /* Ocultar algunos elementos en móvil para ahorrar espacio */
  .table td small.text-muted {
    font-size: 0.65rem;
  }
  
  /* Progress bar más pequeña en móvil */
  .progress {
    width: 60px !important;
  }
  
  /* Desactivar sticky en móvil para mejor experiencia */
  .sticky-top {
    position: relative !important;
    top: auto !important;
  }
}
</style>

